import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { fetchSpacesForUser } from '../../../api/api_users';
import { deleteSpace, fetchHubsForSpace } from '../../../api/api_spaces';
import { fetchPotsForHub } from '../../../api/api_hubs';
import { ResponsiveTitle } from '../../Entities/ResponsiveText';

const SpaceManager = () => {
    const { translations } = useLanguage();
    const [spaces, setSpaces] = useState([]);
    const [error, setError] = useState('');
    const [visibleHubId, setVisibleHubId] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchSpaces = async () => {
            try {
                const token = localStorage.getItem('userToken');
                const response = await fetchSpacesForUser();
                if (response.error) {
                    throw new Error(response.message);
                }
                const spacesData = await Promise.all(response.data.map(async (space) => {
                    const hubsResponse = await fetchHubsForSpace(space.id, token);
                    if (hubsResponse.error) {
                        throw new Error(hubsResponse.message);
                    }
                    const hubsWithPots = await Promise.all(hubsResponse.data.map(async (hub) => {
                        const potsResponse = await fetchPotsForHub(hub.id, token);
                        if (potsResponse.error) {
                            throw new Error(potsResponse.message);
                        }
                        return { ...hub, pots: potsResponse.data || [] };
                    }));
                    return { ...space, hubs: hubsWithPots };
                }));
                setSpaces(spacesData);
            } catch (error) {
                setErrorBasedOnType(error);
            }
        };
        fetchSpaces();
    }, [translations.error_generic, translations.not_authorized, translations.spaces_not_found]);

    const setErrorBasedOnType = (error) => {
        if (error.message.includes('403')) {
            setError(translations.not_authorized);
        } else if (error.message.includes('404')) {
            setError(translations.spaces_not_found);
        } else {
            setError(translations.error_generic + ' ' + error.message);
        }
    };

    const handleDelete = async (id, event) => {
        event.stopPropagation();
        const token = localStorage.getItem('userToken');
        if (window.confirm(translations.confirm_delete_space)) {
            const response = await deleteSpace(id, token);
            if (response.error) {
                setError(translations.error_deleting_space + ': ' + response.message);
            } else {
                setSpaces(prevSpaces => prevSpaces.filter(space => space.id !== id));
            }
        }
    };

    const handleEdit = (space, event) => {
        event.stopPropagation();
        navigate(`/spaces/editSpace/${space.name}`, { state: { space } });
    };

    const handleBack = () => {
        navigate('/home');
    };

    const handleAddSpace = () => {
        navigate('/spaces/newSpace');
    };

    const toggleHubsVisibility = (spaceId) => {
        setVisibleHubId(prevId => prevId === spaceId ? null : spaceId);
    };

    return (
        <div className="sm-page">
            <div className="sm-container">
                <div className="header">
                    <button onClick={handleBack} className="back-button">&#8592;</button>
                    <h1>{translations.my_spaces}</h1>
                    <button onClick={handleAddSpace} className="header-button">{translations.add_space}</button>
                </div>
                {error && <p className="general-error">{error}</p>}
                <div className="sm-space-list">
                    {spaces.map(space => (
                        <li key={space.id} className="sm-space-info">
                            <div className="sm-space-container" onClick={() => toggleHubsVisibility(space.id)}>
                                <div className="sm-space-header">
                                    <ResponsiveTitle text={space.name} style={{ flex: 1 }} className="sm-space-info-title" />
                                    <button onClick={(e) => handleEdit(space, e)} className="general-button edit-button sm-button"><i className="fi fi-rr-pencil"></i></button>
                                    <button onClick={(e) => handleDelete(space.id, e)} className="general-button delete-button sm-button"><i className="fi fi-rr-trash"></i></button>
                                </div>
                                <div>
                                    {space.hubs && space.hubs.length > 0 ? (
                                        <button onClick={(e) => { e.stopPropagation(); toggleHubsVisibility(space.id); }} className="sm-space-info-button">
                                            {translations.hubs} {visibleHubId === space.id ? '↑' : '↓'}
                                        </button>
                                    ) : (
                                        <span>{translations.no_hubs}</span>
                                    )}
                                </div>
                                {visibleHubId === space.id && (
                                    <ul className="sm-hub-list2">
                                        {space.hubs.map(hub => (
                                            <li key={hub.id} className="sm-hub-info">
                                                <span>{hub.name}</span>
                                                <ul className="sm-sensor-list">
                                                    {Array.isArray(hub.pots) && hub.pots.length > 0 ? (
                                                        hub.pots.map(pot => (
                                                            <li key={pot.id} className="sm-sensor-info">
                                                                {translations.pot}: {pot.name}
                                                            </li>
                                                        ))
                                                    ) : (
                                                        <li>{translations.no_pots}</li>
                                                    )}
                                                </ul>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </li>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SpaceManager;
